import { useEffect, useState } from 'react';
import { DateTime } from 'luxon';

import styles from './Feed.module.css';

import { NAVBAR_ICON_URL } from '@/constants';
import { Item } from '@/views/home/stories/TopStories.hooks';
import { Icon } from '@/components/icon/Icon';
import { Menu } from '@/components/menu/Menu';

import { Button } from '../issue/menu-button/MenuButton';
import { useFont } from '../issue/Issue.hooks';
import { useLocation, useNavigate } from '@tanstack/react-router';

export interface FeedState {
	item?: any;
	section?: string;
}

export const FeedView = () => {
	const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
	const location = useLocation();
	const navigate = useNavigate();
	const { fontSize, setFontSize } = useFont();
	const [item, setItem] = useState<Item | null>(null);
	useTitle(item === null ? '' : item.title);
	useEffect(() => {
		const state = location.state as FeedState;

		if (!state) {
			navigate({
				to: '/',
				search: {
					section: 'top-stories',
				},
				replace: true,
			});

			return;
		}

		const { item, section } = state;

		if (!item) {
			navigate({
				to: '/',
				search: {
					section: section || 'top-stories',
				},
				replace: true,
			});

			return;
		}
		setItem(state.item);
	}, [location]);

	const toggleMenu = () => {
		setIsMenuOpen(!isMenuOpen);
	};

	return (
		<div className={styles['feed']}>
			<div className={styles['view']}>
				<Topbar toggleMenu={toggleMenu} />
				<Content item={item} fontSize={fontSize} />
			</div>
			<Menu isOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen}>
				<Button.About />
				<Button.Font fontSize={fontSize} setFontSize={setFontSize} />
			</Menu>
		</div>
	);
};

const Topbar = ({ toggleMenu }: { toggleMenu: () => void }) => {
	const location = useLocation();
	const navigate = useNavigate();
	const state = location.state as FeedState;
	return (
		<div className={styles['topbar']}>
			<div>
				<button
					type="button"
					className={styles['back-button']}
					onClick={() => {
						navigate({
							to: '/',
							search: {
								section: state?.section || 'magazines',
							},
							replace: true,
						});
					}}
				>
					<Icon name={'arrow-left'} color={'#000'} />
				</button>
			</div>
			<div className={styles['title']}>
				<img src={NAVBAR_ICON_URL} alt="Logo" />
			</div>
			<div>
				<MenuButton toggleMenu={toggleMenu} />
			</div>
		</div>
	);
};

function MenuButton({ toggleMenu }: { toggleMenu: () => void }) {
	return (
		<button
			type="button"
			className={['tooltip', 'down', styles['menu']].join(' ')}
			data-text={'Menu'}
			onClick={toggleMenu}
		>
			<Icon id={'menu-btn'} name={'more-horizontal'} color={'#3d3d3d'} />
		</button>
	);
}

const Content = ({ item, fontSize }: ContentProps) => {
	if (!item) {
		return <div className={styles['content']} />;
	}
	const { title, creator, description, pubDate, thumbnail, category } = item;
	const dateTime = DateTime.fromJSDate(pubDate);
	return (
		<article
			className={styles['content']}
			style={{ fontSize: fontSize ? `${fontSize}%` : undefined }}
		>
			<small className={styles['date']}>
				{dateTime.toLocaleString(DateTime.DATETIME_FULL)}
			</small>
			{thumbnail ? (
				<div className={styles['image']}>
					<img src={thumbnail} alt={'Cover'} />
				</div>
			) : null}
			{category ? <h3>{category}</h3> : null}
			<h1>{title}</h1>
			{creator ? (
				<small className={styles['creator']}>
					By <b>{creator}</b>
				</small>
			) : null}
			<div dangerouslySetInnerHTML={{ __html: description }} />
		</article>
	);
};

interface ContentProps {
	item: Item | null;
	fontSize: number;
}

/**
 * React hook that load data from navigation
 *
 */
export function useTitle(title: string | null) {
	useEffect(() => {
		if (!title) {
			return;
		}

		document.title = title;

		const metaHeader = document.querySelector('meta[name="description"]');
		if (metaHeader) {
			metaHeader.setAttribute('content', title);
		}
	}, [title]);
}
